import { useState, useRef, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Separator } from '@/components/ui/separator';
import { Info, Save, RotateCcw, Loader2, Upload, X, Building2, MapPin, Landmark, ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useCompanyDetails } from '@/hooks/use-company-details';
import { compressLogoImage } from '@/lib/compress-image';

// Field Constraints Types
interface FieldConstraintSettings {
  projectCodeMaxLength: number;
  projectNameMaxLength: number;
  descriptionMaxLength: number;
}

const FIELD_CONSTRAINTS = {
  projectCode: { min: 1, max: 30, default: 10 },
  projectName: { min: 1, max: 80, default: 80 },
  description: { min: 1, max: 80, default: 80 },
};

// ============================================================================
// Company Details Section (Task 1.12)
// ============================================================================

function CompanyDetailsSection() {
  const {
    details,
    saveDetails,
    discardChanges,
    isSaving,
    hasUnsavedChanges,
    setField,
    setAddressField,
    setBankField,
    setLogoUrl,
  } = useCompanyDetails();

  // --- Logo drag-and-drop ---
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isCompressing, setIsCompressing] = useState(false);

  const handleLogoFile = useCallback(async (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file (PNG, JPG, SVG, WebP).');
      return;
    }
    setIsCompressing(true);
    try {
      const compressed = await compressLogoImage(file);
      setLogoUrl(compressed);
      toast.success('Logo uploaded successfully.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to process logo.');
    } finally {
      setIsCompressing(false);
    }
  }, [setLogoUrl]);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleLogoFile(file);
  }, [handleLogoFile]);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleLogoFile(file);
    // reset so same file can be re-selected
    e.target.value = '';
  }, [handleLogoFile]);

  const handleRemoveLogo = useCallback(() => {
    setLogoUrl(undefined);
    toast.info('Logo removed.');
  }, [setLogoUrl]);

  // --- Save / discard ---
  const handleSave = useCallback(async () => {
    try {
      await saveDetails();
      toast.success('Company details saved successfully.');
    } catch {
      toast.error('Failed to save company details. Please try again.');
    }
  }, [saveDetails]);

  const handleDiscard = useCallback(() => {
    discardChanges();
    toast.info('Changes discarded.');
  }, [discardChanges]);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Company Details
            </CardTitle>
            <CardDescription>
              Your company information, address and bank details used on invoices
            </CardDescription>
          </div>
          {hasUnsavedChanges && (
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleDiscard} disabled={isSaving}>
                <X className="h-4 w-4 mr-1" />
                Discard
              </Button>
              <Button size="sm" onClick={handleSave} disabled={isSaving}>
                {isSaving ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Save Changes
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-8">

        {/* ── Logo Upload ─────────────────────────────────────────────── */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <ImageIcon className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold">Company Logo</h3>
          </div>

          {details.logoUrl ? (
            /* Preview mode */
            <div className="flex items-start gap-4">
              <div className="border rounded-lg p-3 bg-muted/20 flex items-center justify-center" style={{ minWidth: 160, minHeight: 60 }}>
                <img
                  src={details.logoUrl}
                  alt="Company logo"
                  className="max-h-[80px] max-w-[300px] object-contain"
                />
              </div>
              <div className="flex flex-col gap-2 pt-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isCompressing}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Replace Logo
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRemoveLogo}
                  className="text-destructive hover:text-destructive"
                >
                  <X className="h-4 w-4 mr-2" />
                  Remove
                </Button>
              </div>
            </div>
          ) : (
            /* Drop zone */
            <div
              className={`relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                isDragOver
                  ? 'border-primary bg-primary/5'
                  : 'border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/30'
              }`}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={handleDrop}
            >
              {isCompressing ? (
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">Processing image…</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <Upload className="h-8 w-8 text-muted-foreground" />
                  <p className="text-sm font-medium">Click to upload or drag and drop</p>
                  <p className="text-xs text-muted-foreground">PNG, JPG, SVG, WebP · Max 2 MB · Scaled to 400×120 px</p>
                </div>
              )}
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileInputChange}
          />
        </div>

        <Separator />

        {/* ── Company Information ──────────────────────────────────────── */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold">Company Information</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="cd-name">Company Name <span className="text-destructive">*</span></Label>
              <Input
                id="cd-name"
                value={details.name}
                onChange={(e) => setField('name', e.target.value)}
                placeholder="Acme Ltd"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cd-reg">Company Registration Number</Label>
              <Input
                id="cd-reg"
                value={details.companyRegistrationNumber ?? ''}
                onChange={(e) => setField('companyRegistrationNumber', e.target.value)}
                placeholder="12345678"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cd-vat">VAT Number</Label>
              <Input
                id="cd-vat"
                value={details.vatNumber ?? ''}
                onChange={(e) => setField('vatNumber', e.target.value)}
                placeholder="GB 123 4567 89"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cd-phone">Phone</Label>
              <Input
                id="cd-phone"
                type="tel"
                value={details.phone ?? ''}
                onChange={(e) => setField('phone', e.target.value)}
                placeholder="+44 20 1234 5678"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cd-email">Email</Label>
              <Input
                id="cd-email"
                type="email"
                value={details.email ?? ''}
                onChange={(e) => setField('email', e.target.value)}
                placeholder="accounts@acme.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cd-website">Website</Label>
              <Input
                id="cd-website"
                type="url"
                value={details.website ?? ''}
                onChange={(e) => setField('website', e.target.value)}
                placeholder="https://www.acme.com"
              />
            </div>
          </div>
        </div>

        <Separator />

        {/* ── Registered Address ───────────────────────────────────────── */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold">Registered Address</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2 space-y-2">
              <Label htmlFor="cd-addr1">Address Line 1</Label>
              <Input
                id="cd-addr1"
                value={details.registeredAddress?.line1 ?? ''}
                onChange={(e) => setAddressField('line1', e.target.value)}
                placeholder="123 Business Park"
              />
            </div>
            <div className="sm:col-span-2 space-y-2">
              <Label htmlFor="cd-addr2">Address Line 2</Label>
              <Input
                id="cd-addr2"
                value={details.registeredAddress?.line2 ?? ''}
                onChange={(e) => setAddressField('line2', e.target.value)}
                placeholder="Suite 4"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cd-city">City</Label>
              <Input
                id="cd-city"
                value={details.registeredAddress?.city ?? ''}
                onChange={(e) => setAddressField('city', e.target.value)}
                placeholder="London"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cd-county">County</Label>
              <Input
                id="cd-county"
                value={details.registeredAddress?.county ?? ''}
                onChange={(e) => setAddressField('county', e.target.value)}
                placeholder="Greater London"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cd-postcode">Postcode</Label>
              <Input
                id="cd-postcode"
                value={details.registeredAddress?.postcode ?? ''}
                onChange={(e) => setAddressField('postcode', e.target.value)}
                placeholder="EC1A 1BB"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cd-country">Country</Label>
              <Input
                id="cd-country"
                value={details.registeredAddress?.country ?? ''}
                onChange={(e) => setAddressField('country', e.target.value)}
                placeholder="United Kingdom"
              />
            </div>
          </div>
        </div>

        <Separator />

        {/* ── Bank Details ─────────────────────────────────────────────── */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Landmark className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold">Bank Details</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="cd-bankname">Bank Name</Label>
              <Input
                id="cd-bankname"
                value={details.bankDetails?.bankName ?? ''}
                onChange={(e) => setBankField('bankName', e.target.value)}
                placeholder="Barclays"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cd-accountname">Account Name</Label>
              <Input
                id="cd-accountname"
                value={details.bankDetails?.accountName ?? ''}
                onChange={(e) => setBankField('accountName', e.target.value)}
                placeholder="Acme Ltd"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cd-sortcode">Sort Code</Label>
              <Input
                id="cd-sortcode"
                value={details.bankDetails?.sortCode ?? ''}
                onChange={(e) => setBankField('sortCode', e.target.value)}
                placeholder="20-00-00"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cd-accountnumber">Account Number</Label>
              <Input
                id="cd-accountnumber"
                value={details.bankDetails?.accountNumber ?? ''}
                onChange={(e) => setBankField('accountNumber', e.target.value)}
                placeholder="12345678"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cd-iban">IBAN</Label>
              <Input
                id="cd-iban"
                value={details.bankDetails?.iban ?? ''}
                onChange={(e) => setBankField('iban', e.target.value)}
                placeholder="GB29 NWBK 6016 1331 9268 19"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cd-swift">SWIFT / BIC</Label>
              <Input
                id="cd-swift"
                value={details.bankDetails?.swiftBic ?? ''}
                onChange={(e) => setBankField('swiftBic', e.target.value)}
                placeholder="BARCGB22"
              />
            </div>
          </div>
        </div>

        {/* ── Info note (1.12g) ─────────────────────────────────────────── */}
        <div className="flex items-start gap-2 rounded-md border bg-muted/40 px-4 py-3 text-sm text-muted-foreground">
          <Info className="h-4 w-4 mt-0.5 shrink-0" />
          <span>
            Details are stored locally in your browser and will sync to the server automatically when the backend is connected.
          </span>
        </div>

        {/* ── Bottom save/discard (always visible) ─────────────────────── */}
        <div className="flex justify-end gap-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleDiscard}
            disabled={!hasUnsavedChanges || isSaving}
          >
            <X className="h-4 w-4 mr-1" />
            Discard Changes
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={!hasUnsavedChanges || isSaving}
          >
            {isSaving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save Changes
          </Button>
        </div>

      </CardContent>
    </Card>
  );
}

// ============================================================================
// Main Page
// ============================================================================

export function SettingsGeneralPage() {

  // ===== FIELD CONSTRAINTS STATE =====
  const [fieldSettings, setFieldSettings] = useState<FieldConstraintSettings>({
    projectCodeMaxLength: FIELD_CONSTRAINTS.projectCode.default,
    projectNameMaxLength: FIELD_CONSTRAINTS.projectName.default,
    descriptionMaxLength: FIELD_CONSTRAINTS.description.default,
  });
  const [savedFieldSettings, setSavedFieldSettings] = useState<FieldConstraintSettings>({
    projectCodeMaxLength: FIELD_CONSTRAINTS.projectCode.default,
    projectNameMaxLength: FIELD_CONSTRAINTS.projectName.default,
    descriptionMaxLength: FIELD_CONSTRAINTS.description.default,
  });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Check if there are unsaved changes
  const hasUnsavedFieldConstraintsChanges = 
    fieldSettings.projectCodeMaxLength !== savedFieldSettings.projectCodeMaxLength ||
    fieldSettings.projectNameMaxLength !== savedFieldSettings.projectNameMaxLength ||
    fieldSettings.descriptionMaxLength !== savedFieldSettings.descriptionMaxLength;

  // ===== FIELD CONSTRAINTS HANDLERS =====
  const handleFieldChange = (field: keyof FieldConstraintSettings, value: string) => {
    const numValue = parseInt(value, 10);
    setFieldSettings(prev => ({
      ...prev,
      [field]: isNaN(numValue) ? 0 : numValue,
    }));
    if (fieldErrors[field]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateFieldConstraints = (): boolean => {
    const errors: Record<string, string> = {};

    if (fieldSettings.projectCodeMaxLength < FIELD_CONSTRAINTS.projectCode.min || 
        fieldSettings.projectCodeMaxLength > FIELD_CONSTRAINTS.projectCode.max) {
      errors.projectCodeMaxLength = `Must be between ${FIELD_CONSTRAINTS.projectCode.min} and ${FIELD_CONSTRAINTS.projectCode.max}`;
    }

    if (fieldSettings.projectNameMaxLength < FIELD_CONSTRAINTS.projectName.min || 
        fieldSettings.projectNameMaxLength > FIELD_CONSTRAINTS.projectName.max) {
      errors.projectNameMaxLength = `Must be between ${FIELD_CONSTRAINTS.projectName.min} and ${FIELD_CONSTRAINTS.projectName.max}`;
    }

    if (fieldSettings.descriptionMaxLength < FIELD_CONSTRAINTS.description.min || 
        fieldSettings.descriptionMaxLength > FIELD_CONSTRAINTS.description.max) {
      errors.descriptionMaxLength = `Must be between ${FIELD_CONSTRAINTS.description.min} and ${FIELD_CONSTRAINTS.description.max}`;
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const saveFieldConstraintsChanges = () => {
    if (!validateFieldConstraints()) {
      toast.error('Please fix the errors before saving.');
      return;
    }

    setSavedFieldSettings(fieldSettings);
    toast.success('Field constraint settings have been saved successfully.');
  };

  const resetFieldConstraintsChanges = () => {
    setFieldSettings(savedFieldSettings);
    setFieldErrors({});
    toast.info('Field constraint settings have been reset.');
  };

  const resetFieldConstraintsToDefaults = () => {
    const defaults = {
      projectCodeMaxLength: FIELD_CONSTRAINTS.projectCode.default,
      projectNameMaxLength: FIELD_CONSTRAINTS.projectName.default,
      descriptionMaxLength: FIELD_CONSTRAINTS.description.default,
    };
    setFieldSettings(defaults);
    setSavedFieldSettings(defaults);
    setFieldErrors({});
    toast.info('Field constraints have been reset to default values.');
  };

  // Combined unsaved changes check
  const hasAnyUnsavedChanges = hasUnsavedFieldConstraintsChanges;

  const handleSaveAll = async () => {
    if (hasUnsavedFieldConstraintsChanges) {
      saveFieldConstraintsChanges();
    }
  };

  const handleDiscardAll = () => {
    if (hasUnsavedFieldConstraintsChanges) {
      resetFieldConstraintsChanges();
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Fixed Header with Actions */}
      {hasAnyUnsavedChanges && (
        <div className="sticky top-0 z-10 bg-background border-b">
          <div className="max-w-5xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Info className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  You have unsaved changes
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDiscardAll}
                >
                  Discard Changes
                </Button>
                <Button
                  size="sm"
                  onClick={handleSaveAll}
                >
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        <TooltipProvider>
          <div className="space-y-6">
            <div className="max-w-5xl mx-auto p-6 space-y-6 pb-32">
              {/* Company Details — Task 1.12 */}
              <CompanyDetailsSection />

              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Field Length Constraints</CardTitle>
                      <CardDescription>
                        Configure maximum character lengths for project creation fields
                      </CardDescription>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={resetFieldConstraintsToDefaults}
                    >
                      <RotateCcw className="h-4 w-4 mr-2" />
                      Reset to Defaults
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Project Code */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Label htmlFor="projectCodeMaxLength">
                        Project Code Maximum Length
                      </Label>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button type="button" className="text-muted-foreground hover:text-foreground">
                            <Info className="h-4 w-4" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Range: {FIELD_CONSTRAINTS.projectCode.min} - {FIELD_CONSTRAINTS.projectCode.max} characters (database limit: VARCHAR(30))</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="flex-1 space-y-1">
                        <Input
                          id="projectCodeMaxLength"
                          type="number"
                          min={FIELD_CONSTRAINTS.projectCode.min}
                          max={FIELD_CONSTRAINTS.projectCode.max}
                          value={fieldSettings.projectCodeMaxLength}
                          onChange={(e) => handleFieldChange('projectCodeMaxLength', e.target.value)}
                          className={fieldErrors.projectCodeMaxLength ? 'border-destructive' : ''}
                        />
                        {fieldErrors.projectCodeMaxLength && (
                          <p className="text-sm text-destructive">{fieldErrors.projectCodeMaxLength}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Project Name */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Label htmlFor="projectNameMaxLength">
                        Project Name Maximum Length
                      </Label>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button type="button" className="text-muted-foreground hover:text-foreground">
                            <Info className="h-4 w-4" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Range: {FIELD_CONSTRAINTS.projectName.min} - {FIELD_CONSTRAINTS.projectName.max} characters (database limit: VARCHAR(80))</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="flex-1 space-y-1">
                        <Input
                          id="projectNameMaxLength"
                          type="number"
                          min={FIELD_CONSTRAINTS.projectName.min}
                          max={FIELD_CONSTRAINTS.projectName.max}
                          value={fieldSettings.projectNameMaxLength}
                          onChange={(e) => handleFieldChange('projectNameMaxLength', e.target.value)}
                          className={fieldErrors.projectNameMaxLength ? 'border-destructive' : ''}
                        />
                        {fieldErrors.projectNameMaxLength && (
                          <p className="text-sm text-destructive">{fieldErrors.projectNameMaxLength}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Label htmlFor="descriptionMaxLength">
                        Description Maximum Length
                      </Label>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button type="button" className="text-muted-foreground hover:text-foreground">
                            <Info className="h-4 w-4" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Range: {FIELD_CONSTRAINTS.description.min} - {FIELD_CONSTRAINTS.description.max} characters (database limit: VARCHAR(80))</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="flex-1 space-y-1">
                        <Input
                          id="descriptionMaxLength"
                          type="number"
                          min={FIELD_CONSTRAINTS.description.min}
                          max={FIELD_CONSTRAINTS.description.max}
                          value={fieldSettings.descriptionMaxLength}
                          onChange={(e) => handleFieldChange('descriptionMaxLength', e.target.value)}
                          className={fieldErrors.descriptionMaxLength ? 'border-destructive' : ''}
                        />
                        {fieldErrors.descriptionMaxLength && (
                          <p className="text-sm text-destructive">{fieldErrors.descriptionMaxLength}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

            </div>
          </div>
        </TooltipProvider>
      </div>
    </div>
  );
}

export default SettingsGeneralPage;
